import { useEffect, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Action } from '@reduxjs/toolkit';
import { Dispatch } from 'redux';

import lang from '../../lib/language';
import { PlanPhaseTypeEnum } from '../../lib/models/crm/types';
import { RootState } from '../../lib/store';
import { isEntryFundedAccountUsable } from '../../lib/store/account-flow/selectors';
import { uploadUserPreferencesData } from '../../lib/store/app';
import { isUserPreferencesResolved } from '../../lib/store/app/helpers';
import { AppCall } from '../../lib/store/app/types';
import { normalisePlanPhaseKey } from '../../lib/store/crm/helpers';
import { isCallStatusPending } from '../../lib/util/error-handling/StatusByCallHelpers';

import hurrah from '../../assets/bigImages/hurrah.png';
import { useIsMobile } from '../../hooks';
import BaseModal from '../../shared_components/BaseModal/BaseModal';
import Button from '../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../shared_components/Button/Button.types';
import { MobileDrawer } from '../index';

import styles from './PostChallengeDocumentsApprovedModal.module.scss';

const SHOWN_PREFERENCE_KEY = `${normalisePlanPhaseKey(PlanPhaseTypeEnum.entry)}DocumentsApprovedModalShowed`;

const PostChallengeDocumentsApprovedModal = ({
  isEntryFundedUsable,
  uploadPreferences,
  userPreferencesData,
  isUserPreferencesPending,
  arePreferencesResolved,
}: ReduxProps) => {
  const { isMobile } = useIsMobile();
  const [showModal, setShowModal] = useState<boolean>(false);
  const hasAcknowledged = useRef<boolean>(false);
  const hasOpened = useRef<boolean>(false);
  const isShown: boolean = userPreferencesData ? userPreferencesData[SHOWN_PREFERENCE_KEY] : false;

  useEffect(() => {
    if (!arePreferencesResolved || isUserPreferencesPending || showModal || hasAcknowledged.current) {
      return;
    }
    if (isEntryFundedUsable && !isShown) {
      hasOpened.current = true;
      setShowModal(true);
    }
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    arePreferencesResolved,
    isEntryFundedUsable,
    isShown,
    isUserPreferencesPending,
    showModal,
  ]);
  if (!hasOpened.current && !isEntryFundedUsable) {
    return null;
  }

  const acknowledge = () => {
    if (hasAcknowledged.current) return;
    hasAcknowledged.current = true;
    setShowModal(false);
    uploadPreferences({
      ...userPreferencesData,
      [SHOWN_PREFERENCE_KEY]: true,
    });
  };

  const close = () => setShowModal(false);

  const renderContent = () => (
    <>
      <div className={styles.iconHolder}>
        <img src={hurrah} alt="Thumb icon" />
      </div>
      <div className={styles.textWrapper}>
        <div className={styles.title}>{lang.documentsAfterChallengeIsPassed()}</div>
        <div className={styles.text}>
          <p>{lang.documentsAfterChallengeIsPassedDescription()}</p>
        </div>
      </div>
      <div className={styles.buttonsWrapper}>
        <Button biggerFontSize fullWidth variant={ButtonTypesEnum.Primary} onClick={acknowledge}>
          {lang.commonOkay()}
        </Button>
      </div>
    </>
  );

  return (
    <>
      {isMobile ? (
        <MobileDrawer
          isOpen={showModal}
          title=""
          onClose={close}
          showCloseButton={false}
        >
          {renderContent()}
        </MobileDrawer>
      ) : (
        <BaseModal isOpen={showModal} toggle={close}>
          {renderContent()}
        </BaseModal>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  isEntryFundedUsable: isEntryFundedAccountUsable(state),
  userPreferencesData: state.app.userPreferencesData,
  arePreferencesResolved: isUserPreferencesResolved(state.app),
  isUserPreferencesPending:
    isCallStatusPending(AppCall.uploadUserPreferencesData, state.app.statusByCall)
    || isCallStatusPending(AppCall.getUserPreferencesData, state.app.statusByCall),
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  uploadPreferences: (payload: any) => dispatch(uploadUserPreferencesData(payload)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;
export default connector(PostChallengeDocumentsApprovedModal);
